import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Printer,
  Edit3,
  ArrowLeft,
  Loader2,
  Package,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Truck,
  Building,
  MapPin,
  TrendingUp,
  Activity,
  FileText,
  Download,
  Info,
  Calendar,
  Check,
  User,
  Tag,
  Clock,
  ExternalLink
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewLOI.module.css';
import api, { getStorageUrl } from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import NotFound from '../Error/NotFound';


import { useLoading } from '../../context/LoadingContext';

const ViewLOI = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const pathPrefix = '/projects/lois';
  const labelBase = 'LOI';
  const labelFull = 'LOI (Letter of Intent)';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loi, setLoi] = useState(null);

  const fetchLOI = async () => {
    try {
      const response = await api.get(`/letter-of-intents/${id}`);
      if (response.data.success) {
        setLoi(response.data.data);
      }
    } catch (error) {
      console.error(`Error fetching ${labelBase}:`, error);
      toast.error(`Failed to fetch ${labelFull} details`);
    }
  };

  useEffect(() => {
    fetchLOI();
  }, [id]);



  if (!loi) {
    return <NotFound />;
  }


  // LOIs don't have advance payments in this model
  const totalAmt = parseFloat(loi.total_amount || 0);

  return (
    <div className={styles['view-po-container']}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          {/* Page Header */}
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>{labelBase} Detail</h1>
              <p>Review summary, items, and logistics for this order</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print</span>
              </button>
              <button className={styles['btn-secondary']} onClick={() => navigate(-1)}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => navigate(`${pathPrefix}/edit/${loi.id}`)}>
                <Edit3 size={18} />
                <span>Edit LOI</span>
              </button>
            </div>
          </div>

          {/* Dashboard Header Card */}
          <div className={styles['po-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{loi.loi_number || 'LOI-0000'}</h1>
                <span className={`${styles['status-badge']} ${styles[loi.status] || ''}`}>
                   {loi.status?.toUpperCase() || 'PENDING'}
                </span>
              </div>
            </div>

            <div className={styles['po-meta']}>
              <div className={styles['meta-item']}>
                <Building size={16} />
                <span>Project: <strong>{loi.project?.project_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <User size={16} />
                <span>Vendor: <strong>{loi.vendor?.vendor_name}</strong></span>
              </div>
              <div className={styles['meta-item']}>
                <Calendar size={16} />
                <span>LOI Date: <strong>{loi.loi_date ? new Date(loi.loi_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</strong></span>
              </div>
            </div>
          </div>

          {/* Metrics Row */}
          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total LOI Value</label>
                <h2>₹{totalAmt.toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-tax']}`}><Truck size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Tax Component</label>
                <h2>₹{parseFloat(loi.total_tax_amount || 0).toLocaleString('en-IN')}</h2>
              </div>
            </div>
          </div>

          {/* Content Grid */}
          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              {/* Items Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Package size={20} /></div>
                  <h3>Order Line Items</h3>
                </div>
                
                {/* Desktop view */}
                <div className={styles['table-scroller']}>
                  <table className={styles['data-table']}>
                    <thead>
                      <tr>
                        <th>S.NO</th>
                        <th>ITEM DESCRIPTION</th>
                        <th>UNIT</th>
                        <th>QTY</th>
                        <th>RATE</th>
                        <th className={styles['text-right']}>TOTAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loi.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <div className={styles['item-info']}>
                              <span className={styles['i-name']}>{item.item_name}</span>
                              <span className={styles['i-ref']}>WO Item: #WOI-{item.work_order_item_id}</span>
                            </div>
                          </td>
                          <td className={styles['bold']}>{parseFloat(item.quantity || 0).toLocaleString()} <small>{item.unit?.unit_name}</small></td>
                          <td>₹{parseFloat(item.rate || 0).toLocaleString()}</td>
                          <td>₹{parseFloat(item.tax_amount || 0).toLocaleString()} ({item.tax?.tax_percentage || 0}%)</td>
                          <td className={`${styles['bold']} ${styles['text-right']}`}>₹{parseFloat(item.total_amount || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card-based view */}
                <div className={styles['mobile-items-container']}>
                  {loi.items?.map((item, idx) => (
                    <div className={styles['item-mobile-card']} key={idx}>
                      <div className={styles['m-item-header']}>
                        <span className={styles['m-item-name']}>{item.item_name}</span>
                        <span className={styles['m-item-ref']}>#WOI-{item.work_order_item_id}</span>
                      </div>
                      <div className={styles['m-item-grid']}>
                        <div className={styles['grid-row']}>
                          <div className={styles['grid-col']}>
                            <label>Quantity</label>
                            <span>{parseFloat(item.quantity || 0).toLocaleString()} <small>{item.unit?.unit_name}</small></span>
                          </div>
                          <div className={styles['grid-col']}>
                            <label>Rate</label>
                            <span>₹{parseFloat(item.rate || 0).toLocaleString()}</span>
                          </div>
                        </div>
                        <div className={styles['grid-row']}>
                          <div className={styles['grid-col']}>
                            <label>Tax ({item.tax?.tax_percentage || 0}%)</label>
                            <span>₹{parseFloat(item.tax_amount || 0).toLocaleString()}</span>
                          </div>
                          <div className={styles['grid-col']}>
                            <label>Total Price</label>
                            <span className={styles['mobile-highlight-amt']}>₹{parseFloat(item.total_amount || 0).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions Row */}
              <div className={styles['two-col-row']}>
                <div className={styles['standard-card']}>
                  <div className={styles['card-header']}>
                    <div className={styles['card-icon']}><FileText size={18} /></div>
                    <h3>Vendor Instructions</h3>
                  </div>
                  <p className={styles['note-text']}>{loi.vendor_instructions || 'No specific instructions provided.'}</p>
                </div>
                <div className={styles['standard-card']}>
                  <div className={styles['card-header']}>
                    <div className={styles['card-icon']}><Info size={18} /></div>
                    <h3>Internal Notes</h3>
                  </div>
                  <p className={styles['note-text']}>{loi.internal_notes || 'No internal technical notes.'}</p>
                </div>
              </div>
            </div>

            <div className={styles['right-column']}>
              {/* Stakeholders Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Building size={20} /></div>
                  <h3>Stakeholders</h3>
                </div>
                <div className={styles['stakeholder-list']}>
                  <div className={styles['sh-item']} onClick={() => navigate(`/vendors/view/${loi.vendor_id}`)} style={{ cursor: 'pointer' }}>
                    <div className={styles['sh-avatar']}>V</div>
                    <div className={styles['sh-info']}>
                      <h4>{loi.vendor?.vendor_name}</h4>
                      <p>Supply Side Vendor</p>
                    </div>
                  </div>
                  <div className={styles['sh-item']} onClick={() => navigate(`/projects/${loi.project_id}`)} style={{ cursor: 'pointer' }}>
                    <div className={styles['sh-avatar']} style={{ background: '#fff7ed', color: '#f97316' }}>P</div>
                    <div className={styles['sh-info']}>
                      <h4>{loi.project?.project_name}</h4>
                      <p>Linked Client Project</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Overview */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Activity size={20} /></div>
                  <h3>Financial Summary</h3>
                </div>
                <div className={styles['info-table']}>
                  <div className={styles['s-row']}><span>Subtotal</span><span>₹{parseFloat(loi.subtotal_amount || 0).toLocaleString()}</span></div>
                  <div className={styles['s-row']}><span>Tax Amount</span><span>₹{parseFloat(loi.total_tax_amount || 0).toLocaleString()}</span></div>
                  <div className={`${styles['s-row']} ${styles['highlight']}`} style={{ paddingTop: '20px', marginTop: '8px', borderTop: '1px dashed #e2e8f0' }}>
                    <span>Grand Total</span>
                    <span>₹{totalAmt.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* Timeline Card */}
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['card-icon']}><Clock size={20} /></div>
                  <h3>Order History</h3>
                </div>
                <div className={styles['timeline-list']}>
                  <div className={styles['timeline-item']}>
                    <div className={styles['tl-icon']} style={{ background: '#f0fdf4', color: '#16a34a' }}>
                      <Check size={16} />
                    </div>
                    <div className={styles['tl-content']}>
                      <h4>{labelBase} Created</h4>
                      <p>{loi.created_at ? new Date(loi.created_at).toLocaleDateString() : '—'}</p>
                    </div>
                  </div>
                  <div className={styles['timeline-item']}>
                    <div className={styles['tl-icon']}>
                      <ExternalLink size={16} />
                    </div>
                    <div className={styles['tl-content']}>
                      <h4>Current Status</h4>
                      <p>{loi.status?.toUpperCase() || 'PROCESSED'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Link */}
              {loi.loi_document_filepath && (
                <div className={styles['standard-card']}>
                   <div className={styles['card-header']}>
                    <div className={styles['card-icon']}><Download size={18} /></div>
                    <h3>Documentation</h3>
                  </div>
                  <a 
                    href={getStorageUrl(loi.loi_document_filepath)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={styles['doc-box']}
                  >
                    <div className={styles['doc-icon']}><FileText size={18} /></div>
                    <div className={styles['doc-info']}>
                      <span>{loi.loi_document_filename || `Digital ${labelBase}`}</span>
                      <small>Click to view</small>
                    </div>
                  </a>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default ViewLOI;
